import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Scale, Calculator, BarChart2 } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';
import useLocalStorage from '../../../hooks/useLocalStorage';

// Импорт компонентов калькулятора
import CalculationForm from './ArbitrationCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './ArbitrationCostCalculatorPage/components/ResultsOverview';
import CourtFeeSchedule from './ArbitrationCostCalculatorPage/components/CourtFeeSchedule';
import QuickAdjustmentSidebar from './ArbitrationCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './ArbitrationCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './ArbitrationCostCalculatorPage/components/TipsSidebar';
import InfoContent from './ArbitrationCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  claimAmount: '100000',
  courtType: 'arbitration', // 'arbitration', 'civil', 'administrative', 'commercial'
  claimType: 'property', // 'property', 'non_property', 'contract', 'administrative', 'bankruptcy'
  disputeCategory: 'general', // 'general', 'simplified', 'special'
  plaintiffStatus: 'company', // 'company', 'individual', 'entrepreneur'
  defendantCount: '1',
  includeLawyerFees: false,
  lawyerHourlyRate: '5000',
  estimatedHours: '10',
  includeExpertFees: false,
  expertFeeAmount: '30000',
  includeOtherExpenses: false,
  otherExpensesAmount: '10000',
  applyBenefits: false,
  benefitType: 'none', // 'none', 'social', 'disabled', 'pensioner', 'veteran'
  installmentPlan: false,
  installmentMonths: '3'
};

// Определение типов судов
export const courtTypes = [
  { id: 'arbitration', name: 'Арбитражный суд', description: 'Для споров между юридическими лицами и ИП' },
  { id: 'civil', name: 'Суд общей юрисдикции', description: 'Для гражданских споров с участием физических лиц' },
  { id: 'administrative', name: 'Административный суд', description: 'Для споров с государственными органами' },
  { id: 'commercial', name: 'Коммерческий арбитраж', description: 'Для договорных споров между коммерческими организациями' }
];

// Определение типов исков
export const claimTypes = [
  { id: 'property', name: 'Имущественный спор', description: 'Споры о правах на имущество, взыскании денежных средств' },
  { id: 'non_property', name: 'Неимущественный спор', description: 'Споры, не подлежащие оценке (признание права и т.д.)' },
  { id: 'contract', name: 'Договорной спор', description: 'Споры, вытекающие из договорных правоотношений' },
  { id: 'administrative', name: 'Административный спор', description: 'Оспаривание действий государственных органов' },
  { id: 'bankruptcy', name: 'Дело о банкротстве', description: 'Заявление о признании должника банкротом' }
];

// Определение категорий споров
export const disputeCategories = [
  { id: 'general', name: 'Общий порядок', description: 'Стандартная процедура рассмотрения' },
  { id: 'simplified', name: 'Упрощенный порядок', description: 'Ускоренная процедура для небольших исков' },
  { id: 'special', name: 'Особое производство', description: 'Дела об установлении фактов, имеющих юридическое значение' }
];

// Определение типов льгот
export const benefitTypes = [
  { id: 'none', name: 'Нет льгот', description: 'Стандартный расчет госпошлины' },
  { id: 'social', name: 'Социально уязвимые категории', description: 'Льготы для малоимущих граждан' },
  { id: 'disabled', name: 'Инвалиды', description: 'Льготы для инвалидов I и II групп' },
  { id: 'pensioner', name: 'Пенсионеры', description: 'Льготы для пенсионеров' },
  { id: 'veteran', name: 'Ветераны', description: 'Льготы для ветеранов войны и труда' }
];

/**
 * ArbitrationCostCalculatorPage Component - Калькулятор судебных расходов и государственной пошлины при обращении в арбитражный суд
 */
const ArbitrationCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('arbitration-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости арбитража | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн-калькулятор для расчета государственной пошлины, судебных издержек и полной стоимости арбитражного процесса. Учитывает тип иска, судебную инстанцию, статус истца и другие параметры.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [feeSchedule, setFeeSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useLocalStorage('savedArbitrationCalculations', []);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'arbitration-court':
          newState.courtType = 'arbitration';
          break;
        case 'civil-court':
          newState.courtType = 'civil';
          break;
        case 'property-claim':
          newState.claimType = 'property';
          break;
        case 'contract-dispute':
          newState.claimType = 'contract';
          break;
        case 'simplified-procedure':
          newState.disputeCategory = 'simplified';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setFeeSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Иск на ${formData.claimAmount}₽ (${new Date().toLocaleDateString()})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      courtType: formData.courtType,
      claimType: formData.claimType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // При необходимости можно добавить пересчет шкалы госпошлин
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Scale className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости арбитража'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              feeSchedule={feeSchedule}
              setFeeSchedule={setFeeSchedule}
              courtTypes={courtTypes}
              claimTypes={claimTypes}
              disputeCategories={disputeCategories}
              benefitTypes={benefitTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <CourtFeeSchedule 
                  feeSchedule={feeSchedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Fee Schedule Table */
        .fee-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .fee-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .fee-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .fee-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .fee-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .fee-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .fee-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .fee-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .fee-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default ArbitrationCostCalculatorPage;